import { assignInlineVars } from '@vanilla-extract/dynamic'
import { Screens } from 'patient-api-lib'
import { createRef, Ref, useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useOutlet } from 'react-router-dom'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import {
  checkIsPatientHub,
  getStartup,
  loadConfig,
  loadConfigError,
  selectInitialScreen,
  selectMode,
  setBookingId,
  setError,
} from '../../features/appConfigSlice'
import { isRebooking } from '../../features/bookingFlow/bookingFlowSlice'
import { clearState } from '../../features/promoCode/promoCodeSlice'
import { setSelectedClinic } from '../../features/clinicScreen/clinicScreenSlice'
import {
  selectAutoNavigation,
  selectCurrentScreen,
  setAutoNavigation,
  setCurrentScreen,
  setNavigationStack,
} from '../../features/screensRouterSlice'
import { setSelectedTreatment } from '../../features/treatmentScreen'
import { useAppDispatch } from '../../hooks/useAppDispatch'
import { useAppSelector } from '../../hooks/useAppSelector'
import { useResizeFrame } from '../../hooks/useResizeFrame'
import { MessageType, WidgetMode } from '../../models/enums'
import { getSettingsFromWindow, getWindow } from '../../util/windowUtils'
import LoaderProvider from '../LoaderProvider/LoaderProvider'
import ScreenContainer from '../ScreenContainer/ScreenContainer'
import ScreenHeader from '../ScreenHeader/ScreenHeader'
import SidebarContent from '../SidebarContent/SidebarContent'
import { calculateContainerHeight, showScreenHeader } from './helper'
import {
  containerVariants,
  screenContainerHeight,
  screenMainContainerVariants,
} from './ScreensRouter.css'
import { setSelectedBooking } from '../../features/yourBookingsSlice'

const hostWindow = getWindow()
const { iframeHeight } = getSettingsFromWindow()

const ScreensRouter = () => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const loadApp = useAppSelector(loadConfig)
  const loadError = useAppSelector(loadConfigError)
  const mode = useAppSelector(selectMode)
  const initialScreen = useAppSelector(selectInitialScreen)
  const currentScreen = useAppSelector(selectCurrentScreen)
  const autoNavigation = useAppSelector(selectAutoNavigation)
  const startApp = useAppSelector(getStartup)
  const outlet = useOutlet()
  // Temporary solution to avoid unnecessary rerenders and fix routing :'(
  // https://github.com/remix-run/react-router/issues/7634
  const navigate = useRef(useNavigate())
  const screenRef = useRef<{ [key: string]: Ref<HTMLDivElement> }>({})
  const { containerRef } = useResizeFrame()
  const isRebook = useAppSelector(isRebooking)
  const isPatientHub = useAppSelector(checkIsPatientHub)
  const showSidebar = !isPatientHub

  const screenContainerHeightValue = useMemo(
    () => calculateContainerHeight(mode, iframeHeight),
    [mode]
  )

  const showHeader = useMemo(
    () => showScreenHeader(mode, initialScreen, currentScreen),
    [mode, initialScreen, currentScreen]
  )

  const disableOnScreens = useMemo(
    () => [
      initialScreen,
      Screens.BookingConfirmationScreen,
      Screens.YourBookingsScreen,
      Screens.TokenizationScreen,
    ],
    [initialScreen]
  )

  const showCancelButton = useMemo(
    () =>
      (mode === WidgetMode.Modal && currentScreen === initialScreen) ||
      (mode === WidgetMode.Modal &&
        disableOnScreens.includes(currentScreen as Screens)),
    [currentScreen, initialScreen, mode, disableOnScreens]
  )

  const getScreenRef = useCallback((screenPath: string) => {
    if (screenRef.current[screenPath]) return screenRef.current[screenPath]
    const ref = createRef<HTMLDivElement>()
    screenRef.current[screenPath] = ref
    return ref
  }, [])

  const onBack = useCallback(() => {
    if (!currentScreen) return

    if (
      [
        Screens.ClinicScreen,
        Screens.TreatmentScreen,
        Screens.TreatmentSelectionScreen,
      ].includes(currentScreen)
    ) {
      dispatch(setSelectedTreatment(null))
      dispatch(setSelectedClinic(null))
    }

    if (currentScreen === Screens.BookingContactScreen) {
      dispatch(clearState())
    }

    navigate.current(-1)
  }, [currentScreen, dispatch])

  const onCancel = useCallback(
    () => hostWindow?.postMessage({ type: MessageType.Close }, '*'),
    []
  )

  const reLaunchWidget = useCallback(
    (errorType?: string) => {
      if (!errorType) {
        hostWindow?.postMessage({ type: MessageType.ReloadWidget }, '*')
        window.location.reload()
        return
      }

      if (errorType === 'booking-not-found') {
        hostWindow?.postMessage({ type: MessageType.NotFoundBooking }, '*')
        dispatch(setSelectedBooking(null))
        dispatch(setBookingId(null))
        dispatch(setError(null))
        return
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (autoNavigation && !startApp) {
      navigate.current(`/booking/${currentScreen || initialScreen}`)
      !currentScreen && dispatch(setCurrentScreen(initialScreen as Screens))
      dispatch(setAutoNavigation(false))
      return
    }

    const screen = location.pathname.replace(`/booking/`, '') as Screens
    if (!screen || screen === currentScreen) return

    dispatch(setCurrentScreen(screen))
  }, [
    startApp,
    initialScreen,
    currentScreen,
    location.pathname,
    autoNavigation,
    dispatch,
  ])

  useEffect(() => {
    if (!currentScreen) return
    dispatch(setNavigationStack(currentScreen))
  }, [dispatch, currentScreen, isRebook])

  return (
    <div
      ref={containerRef}
      style={
        isPatientHub
          ? {
              display: 'flex',
              flexDirection: 'column',
              flex: '1',
            }
          : {
              display: 'flex',
              flexDirection: 'column',
              flex: '1',
              height: '100%',
              width: '100%',
            }
      }
    >
      <SwitchTransition>
        <CSSTransition
          key={location.key}
          classNames="screen-transition"
          timeout={300}
          nodeRef={getScreenRef(location.pathname)}
          unmountOnExit
        >
          <LoaderProvider
            enabled={loadApp}
            error={loadError}
            onClickReload={reLaunchWidget}
          >
            <>
              {showHeader && (
                <ScreenHeader
                  showCancelButton={showCancelButton}
                  showBackButton={
                    !disableOnScreens.includes(currentScreen as Screens)
                  }
                  onBack={onBack}
                  onCancel={onCancel}
                  type={mode === WidgetMode.Embedded ? 'transparent' : 'normal'}
                />
              )}
              <div className={mode ? screenMainContainerVariants[mode] : ''}>
                {showSidebar && <SidebarContent onBack={onBack} />}
                <div
                  className={mode ? containerVariants.embedded : ''}
                  style={assignInlineVars({
                    [screenContainerHeight]: screenContainerHeightValue,
                  })}
                >
                  <ScreenContainer ref={getScreenRef(location.pathname)}>
                    <>{outlet}</>
                  </ScreenContainer>
                </div>
              </div>
            </>
          </LoaderProvider>
        </CSSTransition>
      </SwitchTransition>
    </div>
  )
}

export default ScreensRouter
